import DOMPurify from 'dompurify';
import { Marker, Popup } from 'maplibre-gl';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from 'styled-components';

import { ROUTE_START_MARKER_ID, ROUTE_END_MARKER_PREFIX } from '@utils/routeMarkerIds.ts';

import type { AddMarkerPayload, Coordinate, Id, MapInitialized, MapRendererApp, MarkerOptions } from './types.ts';

type MarkerRecord = {
  instance: Marker;
  ordinal: number | null;
  hoverHTML?: string;
  popup?: Popup;
  isGhosted: boolean;
  ghostInstance?: Marker;
};

type MarkerLineStyle = {
  gradient?: string[];
  color?: string;
  width?: number;
};

type DrawLinesPayload = {
  groupName: string;
  ord?: number | null;
  coords: Coordinate[];
  style?: MarkerLineStyle;
};

type AddGhostedMarkerPayload = AddMarkerPayload & {
  markerComponent: React.ReactNode;
  ghostMarkerComponent: React.ReactNode;
  ghostMarkerOptions?: MarkerOptions;
};

export default function MarkerController(app: MapRendererApp, mapInitialized: MapInitialized) {
  const markers: Record<string, MarkerRecord> = {};
  const lineSources: Record<string, { ordinal: number | null | undefined }> = {};
  let navigationMarkersVisible = true;
  const applyNavigationMarkerVisibility = (id: string, instance?: Marker) => {
    if (id === ROUTE_START_MARKER_ID || id.startsWith(ROUTE_END_MARKER_PREFIX)) {
      instance?.getElement().style.setProperty('visibility', navigationMarkersVisible ? '' : 'hidden');
    }
  };
  app.bus.on<{ visible: boolean }>('map/setNavigationMarkersVisible', ({ visible }) => {
    navigationMarkersVisible = visible;
    Object.entries(markers).forEach(([id, marker]) => {
      applyNavigationMarkerVisibility(id, marker.instance);
      applyNavigationMarkerVisibility(id, marker.ghostInstance);
    });
  });

  const toKey = (id: Id) => String(id);

  const logHoverHTMLTelemetry = (hoverHTML: string, sanitized: string) => {
    app.bus.send('appInsights/log', {
      name: 'mapRenderer/hoverHtmlObserved',
      properties: {
        hasHoverHtml: true,
        originalLength: hoverHTML.length,
        sanitizedBecameEmpty: sanitized.length === 0,
        sanitizedChanged: (DOMPurify.removed?.length || 0) > 0,
        sanitizedLength: sanitized.length,
      },
    });
  };

  const renderToDOM = (reactComponent: React.ReactNode) => {
    const el = document.createElement('div');
    const { getTheme } = app.themePack ?? { getTheme: () => undefined };
    const root = createRoot(el);
    root.render(
      <ThemeProvider theme={getTheme() as Parameters<typeof ThemeProvider>[0]['theme']}>
        {reactComponent}
      </ThemeProvider>,
    );
    return el;
  };

  app.bus.on<AddMarkerPayload>(
    'map/addMarker',
    ({ id, ordinal, markerComponent, lng, lat, markerOptions = {}, hoverHTML }) => {
      mapInitialized().then(async map => {
        const key = toKey(id);
        const existingMarker = markers[key];

        if (existingMarker) {
          if (existingMarker.ordinal === ordinal && existingMarker.hoverHTML === hoverHTML) {
            existingMarker.instance.setLngLat([lng, lat]);
            return;
          }

          existingMarker.instance.remove();
        }

        const renderedComponent = markerComponent ? renderToDOM(markerComponent) : undefined;
        const newMarker = new Marker({
          element: renderedComponent,
          anchor: renderedComponent ? 'bottom' : undefined,
          ...markerOptions,
        }).setLngLat([lng, lat]);

        markers[key] = { instance: newMarker, ordinal, hoverHTML, isGhosted: false };
        applyNavigationMarkerVisibility(key, newMarker);
        const currentOrdinal = await app.bus.get<undefined, number | null>('map/getCurrentOrdinal');

        if (hoverHTML && renderedComponent) {
          let popup: Popup | null = null;

          renderedComponent.addEventListener('mouseenter', () => {
            const coordinates: Coordinate = [lng, lat];
            popup?.remove();

            const sanitizedHoverHTML = DOMPurify.sanitize(hoverHTML);
            logHoverHTMLTelemetry(hoverHTML, sanitizedHoverHTML);

            popup = new Popup({
              closeButton: false,
              closeOnClick: false,
              offset: (renderedComponent.getBoundingClientRect().height * (markerOptions.scale || 1)) / 2,
              maxWidth: 'none',
              className: 'MarkerPopup',
            });
            popup.setLngLat(coordinates).setHTML(sanitizedHoverHTML).addTo(map);
            markers[key].popup = popup;
          });

          renderedComponent.addEventListener('mouseleave', () => {
            popup?.remove();
          });
        }

        if (currentOrdinal === ordinal || ordinal === null) {
          newMarker.addTo(map);
        }
      });
    },
  );

  app.bus.on<{ groupName: string }>('map/clearLines', ({ groupName }) => {
    mapInitialized().then(map => {
      const sourceName = lineSourceName(groupName);
      if (map.getSource(sourceName)) {
        map.removeLayer(lineLayerName(groupName));
        map.removeSource(sourceName);
        delete lineSources[sourceName];
      }
    });
  });

  const lineSourceName = (groupName: string) => `drawlines-${groupName}`;
  const lineLayerName = (groupName: string) => `${lineSourceName(groupName)}-layer`;

  app.bus.on<DrawLinesPayload>('map/drawLines', ({ groupName, ord, coords, style = {} }) => {
    mapInitialized().then(async map => {
      const currentOrd = ord === undefined ? await app.bus.get<undefined, number | null>('map/getCurrentOrdinal') : ord;

      if (groupName.length < 1 || !/^[-a-zA-Z0-9]*$/.test(groupName)) {
        throw Error(`Illegal lines group name ${groupName}. Must be alphanumeric only.`);
      }

      const sourceName = lineSourceName(groupName);
      const gradient = style.gradient;

      if (map.getSource(sourceName)) {
        map.removeLayer(lineLayerName(groupName));
        map.removeSource(sourceName);
      }

      map.addSource(sourceName, {
        type: 'geojson',
        lineMetrics: Boolean(gradient),
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: coords,
          },
        },
      });

      const paint: Record<string, unknown> = {
        'line-color': style.color || '#888',
        'line-width': style.width || 8,
      };

      if (gradient) {
        paint['line-gradient'] = [
          'interpolate',
          ['linear'],
          ['line-progress'],
          ...gradient.flatMap((color, index) => [index / (gradient.length - 1), color]),
        ];
      }

      map.addLayer({
        id: lineLayerName(groupName),
        type: 'line',
        minZoom: 0,
        maxZoom: 24,
        source: sourceName,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint,
      } as Parameters<typeof map.addLayer>[0]);

      lineSources[groupName] = { ordinal: currentOrd };
    });
  });

  const removeMarker = (id: Id) => {
    const key = toKey(id);
    const marker = markers[key];
    if (!marker) {
      return;
    }

    marker.popup?.remove();
    marker.instance.remove();
    if (marker.isGhosted) {
      marker.ghostInstance?.remove();
    }
    delete markers[key];
  };

  app.bus.on<AddGhostedMarkerPayload>('map/addGhostedMarker', payload => {
    mapInitialized().then(async map => {
      const key = toKey(payload.id);
      const renderedComponent = renderToDOM(payload.markerComponent);
      const newMarker = new Marker({
        element: renderedComponent,
        anchor: 'bottom',
        ...payload.markerOptions,
      }).setLngLat([payload.lng, payload.lat]);
      const renderedGhostComponent = renderToDOM(payload.ghostMarkerComponent);
      const newGhostMarker = new Marker({
        element: renderedGhostComponent,
        anchor: 'bottom',
        ...payload.ghostMarkerOptions,
      }).setLngLat([payload.lng, payload.lat]);
      const markerElement = newMarker.getElement();
      const ghostMarkerElement = newGhostMarker.getElement();
      applyNavigationMarkerVisibility(key, newMarker);
      applyNavigationMarkerVisibility(key, newGhostMarker);

      markerElement.setAttribute('aria-hidden', 'true');
      ghostMarkerElement.setAttribute('aria-hidden', 'true');

      if (markers[key]) {
        removeMarker(payload.id);
      }

      markers[key] = {
        instance: newMarker,
        ordinal: payload.ordinal,
        isGhosted: true,
        ghostInstance: newGhostMarker,
      };

      const currentOrdinal = await app.bus.get<undefined, number | null>('map/getCurrentOrdinal');
      if (currentOrdinal === payload.ordinal) {
        markers[key].instance.addTo(map);
      } else {
        markers[key].ghostInstance?.addTo(map);
      }
    });
  });

  app.bus.on<{ id: Id }>('map/removeMarker', ({ id }) => {
    mapInitialized().then(() => removeMarker(id));
  });

  app.bus.on<{ ordinal: number | null }>('map/ordinalChanged', ({ ordinal }) => {
    mapInitialized().then(map => {
      Object.values(markers).forEach(marker => {
        if (marker.ordinal === ordinal || marker.ordinal === null) {
          marker.instance.addTo(map);
          if (marker.isGhosted) {
            marker.ghostInstance?.remove();
          }
        } else {
          marker.instance.remove();
          if (marker.isGhosted) {
            marker.ghostInstance?.addTo(map);
          }
        }
      });

      Object.keys(lineSources).forEach(groupName => {
        const layerName = lineLayerName(groupName);
        map.setLayoutProperty(layerName, 'visibility', lineSources[groupName].ordinal === ordinal ? 'visible' : 'none');
      });
    });
  });

  app.bus.on('map/removeAllMarkers', () => {
    mapInitialized().then(() => {
      Object.keys(markers).forEach(id => removeMarker(id));
    });
  });
}
